import React from "react";
import api from "@services/api";
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import "./ask-ai.css";

const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#db2777", "#7c3aed", "#0891b2", "#dc2626", "#65a30d"];
const STAGES = ["Understanding your question…", "Writing a database query…", "Fetching the data…", "Preparing your answer…"];

function numericColumns(columns, rows) {
  if (!rows?.length) return [];
  return columns.filter((c) => rows.every((r) => r[c] === null || r[c] === "" || !isNaN(Number(r[c]))));
}

function toCsv(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
}

function Chart({ chart, columns, rows }) {
  const type = chart?.type || "none";
  if (type === "none" || !rows?.length) return null;

  const x = chart?.x && columns.includes(chart.x) ? chart.x : columns[0];
  let ys = (chart?.y || []).filter((y) => columns.includes(y) && y !== x);
  if (!ys.length) ys = numericColumns(columns, rows).filter((c) => c !== x).slice(0, 3);
  if (!ys.length) return null;

  const data = rows.map((r) => {
    const o = { ...r };
    ys.forEach((y) => (o[y] = Number(o[y])));
    return o;
  });

  if (type === "pie") {
    const y = ys[0];
    return (
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={data} dataKey={y} nameKey={x} cx="50%" cy="50%" outerRadius={110} label>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip /><Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const Comp = type === "line" ? LineChart : BarChart;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <Comp data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey={x} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip /><Legend />
        {ys.map((y, i) =>
          type === "line"
            ? <Line key={y} type="monotone" dataKey={y} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
            : <Bar key={y} dataKey={y} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
        )}
      </Comp>
    </ResponsiveContainer>
  );
}

function DataTable({ columns, rows }) {
  if (!rows?.length) return <div className="ai-muted">No rows.</div>;
  return (
    <div className="ai-table-wrap">
      <table className="ai-table">
        <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map((c) => <td key={c}>{r[c] == null ? "" : String(r[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultCard({ msg }) {
  const [view, setView] = React.useState(null); // null=auto, 'table', 'chart'
  const [showSql, setShowSql] = React.useState(false);
  const { title, answer, sql, chart, columns = [], rows = [], row_count, error } = msg.result || {};

  if (msg.status === "error" || error) {
    return <div className="ai-card ai-error">⚠️ {error || "Something went wrong. Try rephrasing your question."}</div>;
  }

  const hasChart = chart && chart.type && chart.type !== "none" && rows.length > 0;
  const effView = view || (hasChart ? "chart" : "table");

  const download = () => {
    const blob = new Blob([toCsv(columns, rows)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (title || "result").replace(/[^\w]+/g, "_").toLowerCase() + ".csv";
    a.click();
  };

  return (
    <div className="ai-card">
      {title && <div className="ai-card-title">{title}</div>}
      {answer && <div className="ai-answer">{answer}</div>}

      {rows.length > 0 && (
        <>
          <div className="ai-toolbar">
            {hasChart && (
              <div className="ai-toggle">
                <button className={effView === "chart" ? "on" : ""} onClick={() => setView("chart")}>Chart</button>
                <button className={effView === "table" ? "on" : ""} onClick={() => setView("table")}>Table</button>
              </div>
            )}
            <span className="ai-muted">{row_count} row{row_count === 1 ? "" : "s"}</span>
            <div className="ai-spacer" />
            <button className="ai-linkbtn" onClick={download}>⬇ CSV</button>
            <button className="ai-linkbtn" onClick={() => setShowSql((s) => !s)}>{showSql ? "Hide" : "Show"} SQL</button>
          </div>

          {effView === "chart" ? <Chart chart={chart} columns={columns} rows={rows} /> : <DataTable columns={columns} rows={rows} />}

          {showSql && <pre className="ai-sql">{sql}</pre>}
        </>
      )}
    </div>
  );
}

function CommandPreviewCard({ msg, onConfirmed, onCancel }) {
  const [confirming, setConfirming] = React.useState(false);
  const [done, setDone] = React.useState(msg.executed || null);
  const [err, setErr] = React.useState("");
  const preview = msg.preview;
  if (!preview) return null;

  const confirm = async () => {
    setConfirming(true);
    setErr("");
    try {
      const r = await api.post("/ai-insights/command/confirm", { preview_id: preview.preview_id });
      setDone(r.data?.data || { executed_at: new Date().toISOString() });
      if (onConfirmed) onConfirmed(r.data?.data);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to execute command.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="ai-card" style={{ borderLeft: done ? "4px solid #16a34a" : "4px solid #f59e0b" }}>
      <div className="ai-card-title">
        {done ? "✅ Command Executed" : "⚡ Command Preview (Confirmation Required)"}
      </div>
      <div className="ai-answer" style={{ fontWeight: 600 }}>{preview.summary}</div>

      {err && <div className="ai-error" style={{ marginTop: 8, padding: 8 }}>⚠️ {err}</div>}

      <div className="ai-table-wrap" style={{ marginTop: 10 }}>
        <table className="ai-table">
          <thead>
            <tr>
              <th>Target</th>
              <th>Current (Before)</th>
              <th>Proposed (After)</th>
            </tr>
          </thead>
          <tbody>
            {(preview.changes || []).map((c, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: 600 }}>{c.label}</td>
                <td style={{ color: "#64748b" }}>{c.before}</td>
                <td style={{ color: "#15803d", fontWeight: 600 }}>{c.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {done ? (
        <div className="ai-muted" style={{ marginTop: 10 }}>
          Executed by <b>{done.user || preview.user}</b> at {done.executed_at} · Logged to Audit Log.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
          <button className="ai-send sm" onClick={confirm} disabled={confirming}>
            {confirming ? "Executing…" : "Confirm & Execute"}
          </button>
          {onCancel && (
            <button className="ai-linkbtn" onClick={onCancel} disabled={confirming}>
              Cancel
            </button>
          )}
          <span className="ai-muted">No database changes are made until you confirm.</span>
        </div>
      )}
    </div>
  );
}

function CommandPanel({ onClose, onPreviewCreated }) {
  const [actionType, setActionType] = React.useState("add_order_note");
  const [idsText, setIdsText] = React.useState("");
  const [value, setValue] = React.useState("0");
  const [comment, setComment] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submitPreview = async (e) => {
    e.preventDefault();
    setErr("");
    const targetIds = idsText
      .split(/[\s,#]+/)
      .map((v) => parseInt(v, 10))
      .filter((v) => !isNaN(v) && v > 0);
    setLoading(true);
    try {
      const r = await api.post("/ai-insights/command/preview", {
        action_type: actionType,
        target_ids: targetIds,
        value,
        comment,
      });
      if (r.data?.data) {
        onPreviewCreated(r.data.data);
        onClose();
      }
    } catch (ex) {
      setErr(ex?.response?.data?.message || "Could not create command preview.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-rules-overlay" onClick={onClose}>
      <div className="ai-rules-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ai-rules-head">
          <div>
            <h3>⚡ Safe Command Builder</h3>
            <p className="ai-muted">
              Preview a safe admin action (capped at 25 records). You will review the exact Before/After diff before confirming.
            </p>
          </div>
          <button className="ai-x" onClick={onClose}>×</button>
        </div>
        <form className="ai-rules-body" onSubmit={submitPreview}>
          {err && <div className="ai-card ai-error" style={{ marginBottom: 12 }}>⚠️ {err}</div>}

          <div className="ai-rule-edit" style={{ gap: 10, display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Action Type</label>
            <select
              className="ai-input"
              value={actionType}
              onChange={(e) => {
                const next = e.target.value;
                setActionType(next);
                if (next === "update_order_store" || next === "update_customer_store") setValue("0");
                else if (next === "update_order_status") setValue("2");
                else setValue("");
              }}
            >
              <option value="add_order_note">Add Internal Note to Order(s)</option>
              <option value="update_order_store">Reassign Order Store (e.g. WashyWash Facility)</option>
              <option value="update_order_status">Change Order Status</option>
              <option value="update_customer_store">Set Customer Default Store</option>
              <option value="add_ai_rule">Add AI Business Rule</option>
            </select>

            {actionType !== "add_ai_rule" && (
              <>
                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  {actionType === "update_customer_store" ? "Customer ID(s)" : "Order ID(s)"} (comma or space separated, max 25)
                </label>
                <input
                  className="ai-input"
                  placeholder={actionType === "update_customer_store" ? "e.g. 54210, 54211" : "e.g. 237483, 237480"}
                  value={idsText}
                  onChange={(e) => setIdsText(e.target.value)}
                  required
                />
              </>
            )}

            {(actionType === "update_order_store" || actionType === "update_customer_store") && (
              <>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Target Store</label>
                <select className="ai-input" value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="0">0 — WashyWash Facility (Main Hub)</option>
                  <option value="3">3 — Washywash Ghousheh</option>
                  <option value="5">5 — Washywash Embassy</option>
                  <option value="12">12 — Hive Fit Nation</option>
                  <option value="2">2 — MSF Hospital</option>
                  <option value="6">6 — Flash Wash</option>
                </select>
              </>
            )}

            {actionType === "update_order_status" && (
              <>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Target Status</label>
                <select className="ai-input" value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="2">2 — Processing</option>
                  <option value="15">15 — Pick up</option>
                  <option value="17">17 — Cleaning</option>
                  <option value="18">18 — Delivery</option>
                  <option value="5">5 — Complete</option>
                  <option value="21">21 — Skip Selection</option>
                  <option value="22">22 — Reschedule</option>
                  <option value="23">23 — Pending for customer support</option>
                  <option value="7">7 — Canceled</option>
                </select>
              </>
            )}

            {actionType === "add_ai_rule" && (
              <>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Rule Title</label>
                <input
                  className="ai-input"
                  placeholder="e.g. Exclude canceled orders"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  required
                />
              </>
            )}

            <label style={{ fontSize: 13, fontWeight: 600 }}>
              {actionType === "add_order_note"
                ? "Note Text"
                : actionType === "add_ai_rule"
                ? "Rule Instruction"
                : "Reason / Comment (optional)"}
            </label>
            <textarea
              className="ai-input"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                actionType === "add_order_note"
                  ? "Enter internal note to attach to the order…"
                  : actionType === "add_ai_rule"
                  ? "Describe the business rule in plain English or Arabic…"
                  : "Optional reason for audit log…"
              }
              required={actionType === "add_order_note" || actionType === "add_ai_rule"}
            />

            <div className="ai-rule-actions" style={{ marginTop: 8 }}>
              <button className="ai-send sm" type="submit" disabled={loading}>
                {loading ? "Preparing Preview…" : "Preview Changes"}
              </button>
              <button className="ai-linkbtn" type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function AuditLogPanel({ onClose }) {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api
      .get("/ai-insights/audit-log")
      .then((r) => setEntries(r.data?.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="ai-rules-overlay" onClick={onClose}>
      <div className="ai-rules-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ai-rules-head">
          <div>
            <h3>📋 Command Audit Log</h3>
            <p className="ai-muted">Every confirmed command executed via Ask AI is recorded here with Before/After values.</p>
          </div>
          <button className="ai-x" onClick={onClose}>×</button>
        </div>
        <div className="ai-rules-body">
          {loading && <div className="ai-muted">Loading…</div>}
          {!loading && entries.length === 0 && <div className="ai-muted">No commands have been executed yet.</div>}
          {!loading &&
            entries.map((entry) => (
              <div key={entry.id} className="ai-rule" style={{ flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span className="ai-rule-title">{entry.summary}</span>
                  <span className="ai-muted" style={{ fontSize: 12 }}>
                    {entry.user} · {entry.executed_at}
                  </span>
                </div>
                {(entry.changes || []).map((c, idx) => (
                  <div key={idx} style={{ fontSize: 12, color: "#475569", padding: "2px 0" }}>
                    <b>{c.label}:</b> {c.before} → <span style={{ color: "#15803d", fontWeight: 600 }}>{c.after}</span>
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function RulesPanel({ onClose }) {
  const [rules, setRules] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // id being edited, or 'new'
  const [form, setForm] = React.useState({ title: "", rule: "" });

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/ai-insights/rules").then((r) => setRules(r.data?.data || [])).finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm({ title: "", rule: "" }); };
  const startEdit = (r) => { setEditing(r.id); setForm({ title: r.title, rule: r.rule }); };

  const save = async () => {
    if (!form.title.trim() || !form.rule.trim()) return;
    if (editing === "new") await api.post("/ai-insights/rules", form);
    else await api.put(`/ai-insights/rules/${editing}`, form);
    setEditing(null); load();
  };
  const toggle = async (r) => { await api.put(`/ai-insights/rules/${r.id}`, { enabled: r.enabled ? 0 : 1 }); load(); };
  const remove = async (r) => { if (window.confirm(`Delete rule "${r.title}"?`)) { await api.delete(`/ai-insights/rules/${r.id}`); load(); } };

  return (
    <div className="ai-rules-overlay" onClick={onClose}>
      <div className="ai-rules-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ai-rules-head">
          <div>
            <h3>Instructions for the AI</h3>
            <p className="ai-muted">Business rules the AI always applies when answering — e.g. when an order counts as paid. Add or edit one rule at a time.</p>
          </div>
          <button className="ai-x" onClick={onClose}>×</button>
        </div>

        <div className="ai-rules-body">
          {loading && <div className="ai-muted">Loading…</div>}

          {!loading && rules.map((r) => (
            <div key={r.id} className={"ai-rule" + (r.enabled ? "" : " off")}>
              {editing === r.id ? (
                <div className="ai-rule-edit">
                  <input className="ai-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Short title (e.g. When an order is paid)" />
                  <textarea className="ai-input" rows={3} value={form.rule} onChange={(e) => setForm((f) => ({ ...f, rule: e.target.value }))} placeholder="Describe the rule in plain English…" />
                  <div className="ai-rule-actions">
                    <button className="ai-send sm" onClick={save}>Save</button>
                    <button className="ai-linkbtn" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="ai-rule-main">
                    <div className="ai-rule-title">{r.title}</div>
                    <div className="ai-rule-text">{r.rule}</div>
                  </div>
                  <div className="ai-rule-actions">
                    <label className="ai-switch" title={r.enabled ? "Enabled" : "Disabled"}>
                      <input type="checkbox" checked={!!r.enabled} onChange={() => toggle(r)} /> <span>{r.enabled ? "On" : "Off"}</span>
                    </label>
                    <button className="ai-linkbtn" onClick={() => startEdit(r)}>Edit</button>
                    <button className="ai-linkbtn danger" onClick={() => remove(r)}>Delete</button>
                  </div>
                </>
              )}
            </div>
          ))}

          {editing === "new" ? (
            <div className="ai-rule">
              <div className="ai-rule-edit">
                <input className="ai-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Short title (e.g. When an order is paid)" autoFocus />
                <textarea className="ai-input" rows={3} value={form.rule} onChange={(e) => setForm((f) => ({ ...f, rule: e.target.value }))} placeholder="Describe the rule in plain English…" />
                <div className="ai-rule-actions">
                  <button className="ai-send sm" onClick={save}>Add rule</button>
                  <button className="ai-linkbtn" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            <button className="ai-addrule" onClick={startNew}>+ Add a rule</button>
          )}
        </div>
      </div>
    </div>
  );
}

const COMMAND_REGEX = /^(?:add\s+note|move\s+orders?|set\s+orders?|assign\s+orders?|change\s+orders?|update\s+orders?|set\s+customers?|change\s+customers?|update\s+customers?|move\s+customers?|add\s+rule\s*:|teach\s+rule\s*:|(?:اضف|أضف)\s+ملاحظة|(?:انقل|حول|غير|حدث)\s+(?:حالة\s+)?(?:طلب|الطلب)|(?:غير|حدد)\s+(?:عميل|العميل)|(?:اضف|أضف)\s+قاعدة\s*:)/iu;

export default function AskAiPage() {
  const [messages, setMessages] = React.useState([]); // {role, text} | {role:'ai', status, result} | {role:'command_preview', preview}
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [stage, setStage] = React.useState(0);
  const [suggestions, setSuggestions] = React.useState([]);
  const [showRules, setShowRules] = React.useState(false);
  const [showCommand, setShowCommand] = React.useState(false);
  const [showAudit, setShowAudit] = React.useState(false);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/ai-insights/suggestions").then((r) => setSuggestions(r.data?.data || [])).catch(() => {});
  }, []);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, stage]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setStage(0);
    setMessages((m) => [...m, { role: "user", text: q }]);

    if (COMMAND_REGEX.test(q)) {
      try {
        const r = await api.post("/ai-insights/command/preview", { prompt: q });
        if (r.data?.data) {
          setMessages((m) => [...m, { role: "command_preview", preview: r.data.data }]);
        }
      } catch (e) {
        const msg = e?.response?.data?.message || "Could not preview command.";
        setMessages((m) => [...m, { role: "ai", status: "error", result: { error: msg } }]);
      } finally {
        setBusy(false);
      }
      return;
    }

    const stageTimer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 6000);
    try {
      const { data } = await api.post("/ai-insights/ask", { question: q });
      const jobId = data.job_id;
      // Poll for the result.
      let result = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const r = await api.get(`/ai-insights/result/${jobId}`);
        if (r.data.status === "done" || r.data.status === "error") { result = r.data; break; }
      }
      clearInterval(stageTimer);
      if (!result) {
        setMessages((m) => [...m, { role: "ai", status: "error", result: { error: "Timed out. Please try again." } }]);
      } else {
        setMessages((m) => [...m, { role: "ai", status: result.status, result }]);
      }
    } catch (e) {
      clearInterval(stageTimer);
      const msg = e?.response?.data?.message || "Request failed. Please try again.";
      setMessages((m) => [...m, { role: "ai", status: "error", result: { error: msg } }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-page">
      <div className="ai-head">
        <div className="ai-head-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <h2 style={{ marginRight: "auto" }}>✨ Ask AI</h2>
          <button className="ai-instr-btn" onClick={() => setShowCommand(true)}>⚡ Run Command</button>
          <button className="ai-instr-btn" onClick={() => setShowAudit(true)}>📋 Audit Log</button>
          <button className="ai-instr-btn" onClick={() => setShowRules(true)}>⚙ Instructions</button>
        </div>
        <p className="ai-muted">Ask about your orders, revenue, customers or products in plain English or Arabic. The AI reads the database (read-only) and answers with a chart or table. Use <b>⚡ Run Command</b> for preview-and-confirm actions or <b>⚙ Instructions</b> to teach it your business rules.</p>
      </div>

      {showRules && <RulesPanel onClose={() => setShowRules(false)} />}
      {showCommand && (
        <CommandPanel
          onClose={() => setShowCommand(false)}
          onPreviewCreated={(preview) =>
            setMessages((m) => [
              ...m,
              { role: "user", text: `⚡ Command: ${preview.summary}` },
              { role: "command_preview", preview },
            ])
          }
        />
      )}
      {showAudit && <AuditLogPanel onClose={() => setShowAudit(false)} />}

      <div className="ai-chat" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="ai-empty">
            <div className="ai-muted" style={{ marginBottom: 10 }}>Try one of these:</div>
            <div className="ai-chips">
              {suggestions.map((s) => (
                <button key={s} className="ai-chip" onClick={() => ask(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="ai-bubble user">{m.text}</div>
          ) : m.role === "command_preview" ? (
            <CommandPreviewCard
              key={i}
              msg={m}
              onCancel={() =>
                setMessages((prev) => prev.filter((_, idx) => idx !== i))
              }
            />
          ) : (
            <ResultCard key={i} msg={m} />
          )
        )}

        {busy && (
          <div className="ai-card ai-loading">
            <span className="ai-spinner" /> {STAGES[stage]}
          </div>
        )}
      </div>

      <form className="ai-inputbar" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input
          className="ai-input"
          placeholder="Ask a question (EN/AR) or type a command (e.g. Move order #237483 to store 0)…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
        />
        <button className="ai-send" type="submit" disabled={busy || !input.trim()}>
          {busy ? "Working…" : "Ask"}
        </button>
      </form>
    </div>
  );
}
